/**
 * DIE KLADDE (NT-TUTORIAL-KLADDE-01) — geteilte Render-Bausteine für das Home-Panel
 * (components/KladdePanel.ts) und die volle Kladde-Seite (pages/kladde.ts). Eine Wahrheit für
 * Milestone-/Hinweis-Markup, damit beide Stellen nie auseinanderlaufen.
 */

import type { KladdeHintView, KladdeMilestoneView, KladdeProgressView, KladdeStageView } from '../services/kladde.js';
import { formatNox } from './formatters.js';

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Wo im Meilenstein-Fluss ein JIT-Hinweis auftaucht — direkt NACH dem genannten Meilenstein-Key,
 * `null` = ganz am Anfang der Liste. Reine Anzeige-Reihenfolge (kein Backend-Feld) — die
 * Trigger-Wahrheit selbst (`triggered`/`dismissed`) kommt vollständig vom Server.
 */
const HINT_ANCHOR_AFTER_MILESTONE: Readonly<Record<string, string | null>> = {
  currency_split: null,
  wanted_level: 'first_event',
  hideout_grow_or_labor: 'first_property',
  sales_channels: 'first_production',
  geldwaesche: 'first_crime_sale',
};

function renderHintCard(hint: KladdeHintView): string {
  return `
    <div class="kladde-hint-card" data-kladde-hint-key="${esc(hint.key)}">
      <button class="kladde-hint-dismiss" type="button" data-kladde-hint-dismiss="${esc(hint.key)}" aria-label="Hinweis schließen">&#10005;</button>
      <p class="kladde-hint-title">${esc(hint.title)}</p>
      <p class="kladde-hint-body">${esc(hint.body)}</p>
    </div>
  `;
}

function renderMilestoneRow(milestone: KladdeMilestoneView): string {
  return `
    <div class="kladde-milestone${milestone.done ? ' is-done' : ''}${milestone.optional ? ' is-optional' : ''}">
      <span class="kladde-milestone-check" aria-hidden="true">${milestone.done ? '✓' : ''}</span>
      <div class="kladde-milestone-text">
        <p class="kladde-milestone-label">${esc(milestone.label)}${milestone.optional ? ' <span class="kladde-milestone-optional-tag">optional</span>' : ''}</p>
        <p class="kladde-milestone-body">${esc(milestone.body)}</p>
        ${milestone.recommendation ? `<p class="kladde-milestone-recommendation">${esc(milestone.recommendation)}</p>` : ''}
      </div>
    </div>
  `;
}

// Bewusst im gleichen Markup-Gerüst wie renderMilestoneRow() (gleiche Check-Spalte, gleiche
// Label-/Body-Klassen) — soll als ganz normaler Punkt im Fluss wirken, keine Extra-Karte, die
// wie ein Hinweis-Callout aussieht und übersehen wird.
function renderProgressBar(progress: KladdeProgressView): string {
  return `
    <div class="kladde-milestone kladde-milestone--progress${progress.complete ? ' is-done' : ''}">
      <span class="kladde-milestone-check" aria-hidden="true">${progress.complete ? '✓' : ''}</span>
      <div class="kladde-milestone-text">
        <p class="kladde-milestone-label">${esc(progress.label)}</p>
        <p class="kladde-milestone-body">${esc(progress.body)}</p>
        <div class="kladde-progress-bar">
          <div class="kladde-progress-fill" style="width: ${String(progress.percent)}%"></div>
        </div>
        <p class="kladde-progress-meta">${esc(formatNox(progress.currentCents))} / ${esc(formatNox(progress.targetCents))}</p>
      </div>
    </div>
  `;
}

/**
 * Rendert eine Liste sichtbarer Hinweis-Karten (nur triggered && !dismissed) interleaved mit den
 * Meilensteinen der übergebenen Stufen, an ihren Anker-Positionen (siehe HINT_ANCHOR_AFTER_MILESTONE).
 * `hints` wird komplett übergeben (nicht vorgefiltert) — die Funktion filtert selbst.
 */
export function renderStagesWithHints(stages: readonly KladdeStageView[], hints: readonly KladdeHintView[]): string {
  const visibleHints = new Map(hints.filter((h) => h.triggered && !h.dismissed).map((h) => [h.key, h]));

  const parts: string[] = [];

  const topHint = [...visibleHints.values()].find((h) => HINT_ANCHOR_AFTER_MILESTONE[h.key] === null);
  if (topHint) parts.push(renderHintCard(topHint));

  for (const stage of stages) {
    parts.push(`
      <div class="kladde-stage${stage.complete ? ' is-complete' : ''}">
        <p class="kladde-stage-title">${esc(stage.title)}</p>
        <p class="kladde-stage-lede">${esc(stage.lede)}</p>
        <div class="kladde-stage-milestones">
          ${stage.milestones.map((milestone) => {
            const anchoredProgress = stage.progressBars.find((p) => p.anchorBeforeMilestone === milestone.key);
            const anchoredHint = [...visibleHints.values()].find((h) => HINT_ANCHOR_AFTER_MILESTONE[h.key] === milestone.key);
            return (anchoredProgress ? renderProgressBar(anchoredProgress) : '') + renderMilestoneRow(milestone) + (anchoredHint ? renderHintCard(anchoredHint) : '');
          }).join('')}
        </div>
      </div>
    `);
  }

  return parts.join('');
}

/** Reines Glossar — alle Hinweise unabhängig von triggered/dismissed, dauerhaft nachlesbar.
 * id-Attribut (NT-VIERTEL-07-FEINSCHLIFF, Teil C) ermöglicht das Anspringen eines einzelnen
 * Eintrags per Deep-Link, z.B. aus dem Fragezeichen im Viertel-Tab (`/kladde?view=spickzettel&
 * entry=das_viertel`, siehe pages/kladde.ts). */
export function renderSpickzettel(hints: readonly KladdeHintView[]): string {
  return hints.map((hint) => `
    <div class="kladde-spickzettel-entry" id="kladde-spickzettel-${esc(hint.key)}">
      <p class="kladde-spickzettel-title">${esc(hint.title)}</p>
      <p class="kladde-spickzettel-body">${esc(hint.body)}</p>
    </div>
  `).join('');
}
